import { useEffect, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  ORG_SUBSCRIPTION_CATALOG,
  ORG_SUBSCRIPTION_NOTE_MAX,
  SUBSCRIPTION_KEYS,
  isKnownSubscriptionKey,
  isSubscriptionActive,
  type OrgEntitlementRow,
  type OrgSubscriptionCatalogEntry,
  type OrgSubscriptionStatus,
} from "@/lib/platform/orgSubscriptionCatalog";
import type { PlatformOrgRow } from "./platformPanelTypes";
import { useOrgSubscriptions } from "./useOrgSubscriptions";

const fieldClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100";

function statusLabel(row: OrgEntitlementRow | undefined): string {
  if (!row) return "Not granted";
  return row.status === "active" ? "Active" : "Inactive";
}

function GrantControl(props: {
  entry: OrgSubscriptionCatalogEntry;
  row: OrgEntitlementRow | undefined;
  salesActive: boolean;
  suiteActive: boolean;
  busy: boolean;
  onSave: (status: OrgSubscriptionStatus, notes: string) => void;
}) {
  const savedNote = props.row?.notes ?? "";
  const [note, setNote] = useState(savedNote);
  useEffect(() => {
    setNote(savedNote);
  }, [savedNote, props.row?.updated_at]);
  const active = props.row?.status === "active";
  const noteDirty = note.trim() !== savedNote.trim();

  function toggle() {
    const next: OrgSubscriptionStatus = active ? "inactive" : "active";
    if (next === "inactive" && !props.row) return;
    props.onSave(next, note);
  }

  function saveNote() {
    if (!props.row) return;
    props.onSave(active ? "active" : "inactive", note);
  }

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-zinc-700">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-gray-900 dark:text-zinc-100">{props.entry.label}</div>
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">{props.entry.implication}</p>
          {props.entry.key === SUBSCRIPTION_KEYS.turboSign && props.salesActive ? (
            <p className="mt-1 text-sm text-gray-800 dark:text-zinc-200">
              Turbo Sales is active for this organization, so Sign is already on.
            </p>
          ) : null}
          {props.entry.key === SUBSCRIPTION_KEYS.turboManufacturing && props.suiteActive && !active ? (
            <p className="mt-1 text-sm text-gray-800 dark:text-zinc-200">
              Manufacturing Suite is active, so Turbo Manufacturing is already on.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={active}
          aria-label={`${props.entry.label}, ${statusLabel(props.row)}`}
          disabled={props.busy}
          onClick={toggle}
          className={
            "shrink-0 rounded-full px-3 py-1 text-sm font-medium " +
            (active
              ? "bg-indigo-600 text-white"
              : "border border-gray-300 bg-white text-gray-700 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200")
          }
        >
          {statusLabel(props.row)}
        </button>
      </div>
      <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-zinc-400">
        Note
        <input
          className={`${fieldClass} mt-1`}
          value={note}
          maxLength={ORG_SUBSCRIPTION_NOTE_MAX}
          onChange={(e) => setNote(e.target.value)}
          disabled={props.busy}
        />
      </label>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          disabled={props.busy || !props.row || !noteDirty}
          onClick={saveNote}
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
        >
          Save note
        </button>
      </div>
    </div>
  );
}

function ReadOnlyRow(props: { row: OrgEntitlementRow; detail: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-zinc-700 dark:bg-zinc-900/40">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-gray-900 dark:text-zinc-100">{props.row.addon_key}</div>
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">{props.detail}</p>
          {props.row.notes ? (
            <p className="mt-1 text-sm text-gray-800 dark:text-zinc-200">Note: {props.row.notes}</p>
          ) : null}
        </div>
        <span className="shrink-0 text-sm text-gray-600 dark:text-zinc-300">{statusLabel(props.row)}</span>
      </div>
    </div>
  );
}

export function PlatformOrgSubscriptionsPanel({ orgs }: { orgs: PlatformOrgRow[] }) {
  const [organizationId, setOrganizationId] = useState("");
  const subs = useOrgSubscriptions(organizationId);
  const sortedOrgs = [...orgs].sort((a, b) => a.name.localeCompare(b.name));
  const salesActive = isSubscriptionActive(subs.rows, SUBSCRIPTION_KEYS.turboSales);
  const suiteActive = isSubscriptionActive(subs.rows, SUBSCRIPTION_KEYS.legacyMfgSuite);
  const legacyRow = subs.rows.find((row) => row.addon_key === SUBSCRIPTION_KEYS.legacyMfgSuite);
  const unknownRows = subs.rows.filter((row) => !isKnownSubscriptionKey(row.addon_key));
  const grantable = ORG_SUBSCRIPTION_CATALOG.filter((entry) => entry.grantable);

  return (
    <SectionCard
      title="Organization subscriptions"
      description="Turn a product on or off for one organization. Inactive keeps the row. Nothing here is granted until you change it."
    >
      <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
        Organization
        <select
          className={`${fieldClass} mt-1 max-w-md`}
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
        >
          <option value="">Select an organization</option>
          {sortedOrgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
              {org.is_active === false ? " (inactive)" : ""}
            </option>
          ))}
        </select>
      </label>

      {subs.error ? <p className="text-sm text-red-700 dark:text-red-300">{subs.error}</p> : null}
      {!organizationId ? (
        <p className="text-sm text-gray-600 dark:text-zinc-300">Choose an organization to see its entitlement rows.</p>
      ) : null}
      {organizationId && subs.loading ? (
        <p className="text-sm text-gray-600 dark:text-zinc-300">Loading entitlements…</p>
      ) : null}

      {organizationId && !subs.loading ? (
        <div className="space-y-3">
          {subs.rows.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-zinc-300">No entitlement rows stored for this organization yet.</p>
          ) : null}
          {grantable.map((entry) => (
            <GrantControl
              key={`${organizationId}:${entry.key}`}
              entry={entry}
              row={subs.rows.find((row) => row.addon_key === entry.key)}
              salesActive={salesActive}
              suiteActive={suiteActive}
              busy={subs.busyKey === entry.key}
              onSave={(status, notes) => void subs.save(entry.key, status, notes)}
            />
          ))}
          {legacyRow ? (
            <ReadOnlyRow
              row={legacyRow}
              detail="Legacy alias. An active row folds into Turbo Manufacturing only. This screen does not grant it."
            />
          ) : null}
          {unknownRows.map((row) => (
            <ReadOnlyRow key={row.id} row={row} detail="Outside the subscription catalog. This screen cannot change it." />
          ))}
        </div>
      ) : null}
    </SectionCard>
  );
}
